import { isBoolean, isNumber } from 'lodash';
import { getRotatePosition } from '@/utils/collision-kit';
import { traverseTree } from '@/utils/tree-kit';
import { IGroupLayerProps, ILayerProps, IPoint, LayerType } from '@/version-types';
import { getBehavior } from '../behavior';
import { parseLayerId, resetGroupLayerRotate } from '../utils';

/** 扁平图层数据 */
export class FlatLayerMap {
    identity: string;
    /** 图层数据 */
    data: Record<string, ILayerProps> = {};
    /** 改变的 id 集合 */
    rerenderIdsMap: Map<number, Set<string>> = new Map();
    /** 需要刷新的舞台原始索引 */
    rerenderOriginIndexes: Set<number> = new Set();
    /** 舞台尺寸变化原始索引 */
    resizeOriginIndexes: Set<number> = new Set();
    /** 图层变化回调 */
    onLayerChange: (layerId?: string) => void;
    /** 正在编编辑的图层ID */
    private textEditingId: string;

    constructor(identity: string, onLayerChange: (layerId?: string) => void) {
        this.identity = identity;
        this.onLayerChange = onLayerChange;
        this.textEditingId = '';
    }

    /** 销毁 */
    destroy() {
        this.data = {};
        this.textEditingId = '';
        this.rerenderIdsMap.clear();
        this.rerenderOriginIndexes.clear();
        this.resizeOriginIndexes.clear();
    }

    /** 检查图层是否是临时组 */
    checkIsExtraGroup(layer: ILayerProps) {
        if (this.checkIsGroup(layer) && (layer.data as IGroupLayerProps).isExtraGroup) {
            return true;
        }
        return false;
    }

    /** 检查图层是否是正式组 */
    checkIsFormalGroup(layer: ILayerProps) {
        if (this.checkIsGroup(layer) && !(layer.data as IGroupLayerProps).isExtraGroup) {
            return true;
        }
        return false;
    }

    /** 检查图层是否是组 */
    checkIsGroup(layer: ILayerProps) {
        return layer?.layerType === LayerType.GROUP_LAYER;
    }

    /** 检查图层是否是背景图层 */
    checkIsBackground(layer: ILayerProps) {
        return !!getBehavior(this.identity, layer)?.isBackgroundLayer?.();
    }

    /** 检查图层是否是主图层 */
    checkIsMain(layer: ILayerProps) {
        return !!getBehavior(this.identity, layer)?.isMainLayer?.();
    }

    /**
     * 获取外壳图层
     *
     * @param id - 图层 ID
     * @param extraGroupIsWrap - 可选；临时组是否可以作为外壳；默认值为 `false`
     */
    getWrapLayer(layerId: string, extraGroupIsWrap = false) {
        let wrapLayer = this.get(layerId);
        if (!wrapLayer) return null;
        const updateWrapLayer = (id: string, currentLayer: ILayerProps) => {
            const layer = this.get(id);
            if (!layer) {
                wrapLayer = currentLayer;
                return;
            }

            if (extraGroupIsWrap) {
                if (this.checkIsExtraGroup(layer)) {
                    // wrapLayer = currentLayer;
                    wrapLayer = layer;
                    return;
                }
            }

            if (layer.parentId) {
                updateWrapLayer(layer.parentId, layer);
            } else {
                wrapLayer = layer;
            }
        };

        if (wrapLayer?.parentId) {
            updateWrapLayer(wrapLayer.parentId, wrapLayer);
        }
        // console.log('updateWrapLayer', wrapLayer);

        return wrapLayer;
    }

    get(layerId: string) {
        // console.log(this.identity, this.data);
        return this.data[layerId];
    }

    set(layerId: string, layer: ILayerProps, rerender = true, updateDependencies = true) {
        const isBackgroundLayer = getBehavior(this.identity, layer)?.isBackgroundLayer?.();
        let isSizeChange = false;
        if (isBackgroundLayer) {
            const oldLayer = this.get(layerId);
            if (!oldLayer || oldLayer.width !== layer.width || oldLayer.height !== layer.height) {
                isSizeChange = true;
            }
        }
        if (isBoolean(layer.__isOnTextEditing)) {
            if (layer.__isOnTextEditing) {
                this.textEditingId = layer.id;
            } else if (this.textEditingId === layer.id) {
                this.textEditingId = '';
            }
        } else if (this.textEditingId === layer.id) {
            layer.__isOnTextEditing = true;
        }
        this.data[layerId] = layer;
        this.onLayerChange(layerId);
        if (updateDependencies) {
            const groupId = layer.parentId || (this.checkIsGroup(layer) ? layer.id : null);
            if (groupId) {
                // 重置父图层的大小并更新引用
                const updateParent = (id: string) => {
                    const layer = this.get(id);
                    if (!layer) return;
                    const positions: IPoint[] = [];
                    (layer.children || []).forEach((e: ILayerProps) => {
                        const item = this.get(e.id);
                        if (!item) return;
                        const position = getRotatePosition(item);
                        positions.push(...position);
                    });
                    if (!positions.length) return;
                    const newLayer = { ...layer };
                    resetGroupLayerRotate(newLayer, positions);
                    this.data[id] = newLayer;
                    // 更新父图层中的子图层引用
                    if (newLayer.parentId) {
                        const parentLayer = this.get(newLayer.parentId);
                        if (parentLayer && parentLayer.children) {
                            const index = parentLayer.children.findIndex((child) => child.id === id);
                            if (index !== -1) {
                                parentLayer.children[index] = newLayer;
                            }
                        }
                        // 递归更新上层组
                        updateParent(newLayer.parentId);
                    }
                };
                const layer = this.get(groupId);
                // 找出最底层叶子结点
                const parentInfo: Map<string, boolean> = new Map();
                traverseTree(layer, (node) => {
                    if (!node) return;
                    if (!node.children || node.children.length === 0) {
                        if (node.parentId) {
                            if (!parentInfo.has(node.parentId)) {
                                parentInfo.set(node.parentId, true);
                            }
                        }
                    } else {
                        if (node.parentId) {
                            parentInfo.set(node.parentId, false);
                        }
                    }
                });
                for (const [key, value] of parentInfo.entries()) {
                    if (value) {
                        // 更新父图层
                        updateParent(key);
                    }
                }
                // 更新所有子图层的引用
                const updateChildrenRefs = (parentLayer: ILayerProps) => {
                    if (!parentLayer.children) return;
                    parentLayer.children = parentLayer.children.map((child) => {
                        const updatedChild = this.data[child.id];
                        if (updatedChild.children) {
                            updateChildrenRefs(updatedChild);
                        }
                        return updatedChild;
                    });
                };
                // 从根节点开始更新引用
                const rootLayer = this.getWrapLayer(layerId, true);
                if (rootLayer) {
                    updateChildrenRefs(rootLayer);
                }
            }
        }
        if (!rerender) return;
        const { originIndex } = parseLayerId(layerId);
        if (isNumber(originIndex)) {
            if (isSizeChange) {
                this.resizeOriginIndexes.add(originIndex);
                // console.log('isSizeChange', cloneDeep(this.resizeOriginIndexes));
            }
            if (this.rerenderOriginIndexes.has(originIndex)) return;
            const ids = this.rerenderIdsMap.get(originIndex);
            const parentLayer = this.getWrapLayer(layerId, true);
            if (parentLayer) {
                if (ids) {
                    if (this.checkIsExtraGroup(parentLayer)) {
                        ids.add(layerId);
                    } else {
                        ids.add(parentLayer.id);
                    }
                } else {
                    let updateLayerId = layerId;
                    if (this.checkIsFormalGroup(parentLayer)) {
                        updateLayerId = parentLayer.id;
                    }
                    this.rerenderIdsMap.set(originIndex, new Set([updateLayerId]));
                }
            }
        }
    }

    delete(layerId: string, emitEvent = true) {
        const { originIndex } = parseLayerId(layerId);
        if (isNumber(originIndex)) {
            this.rerenderOriginIndexes.add(originIndex);
        }
        delete this.data[layerId];
        if (layerId === this.textEditingId) {
            this.textEditingId = '';
        }
        if (emitEvent) {
            this.onLayerChange();
        }
    }

    setRenderLayerIds(layerIds: string[]) {
        layerIds.forEach((layerId) => {
            const { originIndex } = parseLayerId(layerId);
            if (isNumber(originIndex)) {
                const ids = this.rerenderIdsMap.get(originIndex);
                if (ids) {
                    ids.add(layerId);
                } else {
                    this.rerenderIdsMap.set(originIndex, new Set([layerId]));
                }
            }
        });
    }
}
